
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@client/shared/auth';
import type { CellState } from '@client/shared/calendar/slot-grid';
import {
  cellsToRanges,
  rangeDayCount,
  useDayViewWindowSnap,
  useSlotCalendarGrid,
  useSlotSelectionMode,
  type CellKey,
  type SlotCalendarRange,
} from '@client/shared/calendar/slot-grid';
import {
  addDays as addDaysInstant,
  addWeeks,
  startOfDay,
  startOfWeek,
} from '@client/shared/calendar/week-grid.ts';
import { useBreakpoint } from '@client/shared/hooks/use-media-query.ts';
import { SLOT_MINUTES, SLOTS_PER_DAY } from '@shared/booking';
import { useAgentNameMap, useAgents, type Agent } from '../agent-operations.ts';
import {
  cellStatus,
  cellStartInstant,
  selectionToBooking,
  rangeConflicts,
  toRange,
  type SelectionResult,
} from '@client/shared/calendar/booking-grid.ts';
import { editableSpan } from '@client/shared/calendar/editable-span.ts';
import type { EditTimes, EditBounds } from '@client/shared/calendar/editable-span.ts';
import { bookSlot, type BookSlotResult } from './book-slot-operation.ts';
import { addUnavailability } from './add-unavailability-operation.ts';
import { listingDetailsKey } from './listing-details-operations.ts';
import type { ListingDetail } from './listing-details-operations.ts';
import { toBookingEvents, toUnavailabilityBlocks } from './listing-details-operations.ts';

const VISIBLE_START_MIN = 7 * 60;
const VISIBLE_END_MIN = 21 * 60;
export const VISIBLE_START_SLOT = VISIBLE_START_MIN / SLOT_MINUTES;
export const VISIBLE_END_SLOT = VISIBLE_END_MIN / SLOT_MINUTES;

export type CalendarMode = 'book' | 'block';

export type ActionFlow =
  | { kind: 'idle' }
  | { kind: 'booked'; range: { start: string; end: string }; forAgent: string | null }
  | { kind: 'blocked'; count: number }
  | { kind: 'slot_taken'; message: string }
  | { kind: 'outside_window'; message: string }
  | { kind: 'conflict' }
  | { kind: 'error'; message: string };

export type SettledFlow = Extract<ActionFlow, { kind: 'booked' } | { kind: 'blocked' }>;
export type EditingFlow = Exclude<ActionFlow, SettledFlow>;

export interface PopoverPoint {
  readonly x: number;
  readonly y: number;
}

export type { EditTimes, EditBounds };


interface IntakeState {
  readonly painted: ReadonlySet<CellKey>;
  readonly mode: CalendarMode;
  readonly assignee: string | null;
  readonly anchor: PopoverPoint | null;
  readonly flow: ActionFlow;
}

type IntakeAction =
  | { type: 'nav_reset' }
  | { type: 'paint'; cells: ReadonlySet<CellKey> }
  | { type: 'repaint'; cells: ReadonlySet<CellKey> }
  | { type: 'release'; anchor: PopoverPoint | null }
  | { type: 'set_mode'; mode: CalendarMode }
  | { type: 'set_assignee'; assignee: string | null }
  | { type: 'result'; flow: Exclude<ActionFlow, { kind: 'idle' }> }
  | { type: 'close' };

const freshState = (): IntakeState => ({
  painted: new Set(),
  mode: 'book',
  assignee: null,
  anchor: null,
  flow: { kind: 'idle' },
});

function reduce(state: IntakeState, action: IntakeAction): IntakeState {
  switch (action.type) {
    case 'nav_reset':
      return { ...freshState(), mode: state.mode };
    case 'paint':
      return { ...state, painted: action.cells, anchor: null, flow: { kind: 'idle' } };
    case 'repaint':
      return { ...state, painted: action.cells, flow: { kind: 'idle' } };
    case 'release':
      return state.painted.size === 0 || action.anchor === null
        ? { ...state, anchor: null, flow: { kind: 'idle' } }
        : { ...state, anchor: action.anchor, flow: { kind: 'idle' } };
    case 'set_mode':
      return action.mode === state.mode
        ? state
        : { ...state, mode: action.mode, flow: { kind: 'idle' } };
    case 'set_assignee':
      return { ...state, assignee: action.assignee };
    case 'result':
      return action.flow.kind === 'booked' || action.flow.kind === 'blocked'
        ? { ...state, painted: new Set(), flow: action.flow }
        : { ...state, flow: action.flow };
    case 'close':
      return { ...state, painted: new Set(), anchor: null, flow: { kind: 'idle' } };
  }
}


export interface UseBookingIntakeArgs {
  listing: ListingDetail;
  canBlock?: boolean;
  canBookForOthers?: boolean;
  /** Fires once each time a booking lands (used to complete the first-run tutorial). */
  onBooked?: () => void;
}

export function useBookingIntake({
  listing,
  canBlock = false,
  canBookForOthers = false,
  onBooked,
}: UseBookingIntakeArgs) {
  const queryClient = useQueryClient();
  const zone = listing.timezone ?? null;
  // Single source of truth for the window end: the server-resolved instant (rolling OR fixed-date).
  const windowEnd = useMemo(
    () => new Date(listing.bookingWindowEnd),
    [listing.bookingWindowEnd],
  );
  const session = useCurrentUser();
  const viewerUserId = session?.user.id ?? null;
  // Bookability is the LITERAL agent role, not the ladder (docs/auth-and-session.md).
  // The session, not the async agents query, is the source: it's always loaded and
  // implies is_active, so a bookable viewer never flickers non-bookable mid-fetch.
  const viewerBookable = session?.roles.includes('agent') ?? false;
  const nameMap = useAgentNameMap();
  const agents: readonly Agent[] = useAgents();

  const tapMode = useSlotSelectionMode() === 'tap';

  const [state, dispatch] = useReducer(reduce, undefined, freshState);
  const mode: CalendarMode = canBlock && state.mode === 'block' ? 'block' : 'book';

  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [chosenRange, setRange] = useState<SlotCalendarRange>('week');

  const breakpoint = useBreakpoint();
  const rangeForced = breakpoint !== 'desktop';
  const range: SlotCalendarRange =
    breakpoint === 'phone' ? 'day' : rangeForced ? '3day' : chosenRange;

  const { days, columns, rows, window, inWindow, canStepBack, canStepForward } = useSlotCalendarGrid({
    zone: zone ?? 'UTC',
    anchor,
    range,
    windowEnd,
    visibleStartMin: VISIBLE_START_MIN,
    visibleEndMin: VISIBLE_END_MIN,
  });

  const step = useCallback(
    (delta: -1 | 1) => {
      const tz = zone ?? 'UTC';
      setAnchor((prev) =>
        range === 'week'
          ? addWeeks(startOfWeek(prev, tz), tz, delta)
          : addDaysInstant(startOfDay(prev, tz), tz, delta * rangeDayCount(range)),
      );
    },
    [zone, range],
  );
  const goToToday = useCallback(() => setAnchor(new Date()), []);

  useDayViewWindowSnap({ range, days, inWindow, onSnap: goToToday });

  const bookings = useMemo(
    () => toBookingEvents(listing).map((b) => toRange({ start: b.start, end: b.end })),
    [listing],
  );
  const unavailability = useMemo(
    () => toUnavailabilityBlocks(listing).map((u) => toRange({ start: u.start, end: u.end })),
    [listing],
  );

  const statusByCell = useMemo(() => {
    const map = new Map<CellKey, Exclude<CellState, 'selected'>>();
    days.forEach((date, dayIdx) => {
      for (let slot = 0; slot < SLOTS_PER_DAY; slot++) {
        const start = cellStartInstant(date, slot, zone ?? '');
        map.set(`${dayIdx}:${slot}`, cellStatus(start, window, bookings, unavailability));
      }
    });
    return map;
  }, [days, zone, window, bookings, unavailability]);

  const cellState = useCallback(
    (day: number, slot: number): CellState => statusByCell.get(`${day}:${slot}`) ?? 'free',
    [statusByCell],
  );

  const daysKey = days.join('|');
  useEffect(() => {
    dispatch({ type: 'nav_reset' });
  }, [daysKey]);

  const gridWrapRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const cardOpen = state.anchor != null;
  useEffect(() => {
    if (!cardOpen) return;
    const onDocDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (cardRef.current?.contains(t) || gridWrapRef.current?.contains(t)) return;
      if (t instanceof Element && t.closest('[role="listbox"]')) return;
      dispatch({ type: 'close' });
    };
    document.addEventListener('pointerdown', onDocDown);
    return () => document.removeEventListener('pointerdown', onDocDown);
  }, [cardOpen]);

  const selection: SelectionResult = useMemo(
    () => selectionToBooking(state.painted, days, zone ?? ''),
    [state.painted, days, zone],
  );

  const assignee = state.assignee;
  // A non-bookable viewer (admin/owner without the agent role) must pick a real agent.
  const needsAssignee = canBookForOthers && assignee == null && !viewerBookable;
  const assigneeLabel =
    canBookForOthers && assignee && assignee !== viewerUserId
      ? (nameMap.get(assignee) ?? 'the selected agent')
      : null;

  const blockRanges = useMemo(
    () => (state.painted.size === 0 ? [] : cellsToRanges(state.painted, days, zone ?? '')),
    [state.painted, days, zone],
  );

  const span = useMemo(
    () => editableSpan(state.painted, days, zone ?? '', cellState, {
      startSlot: VISIBLE_START_SLOT,
      endSlot: VISIBLE_END_SLOT,
    }),
    [state.painted, days, zone, cellState],
  );
  const editTimes: EditTimes | null = span?.times ?? null;
  const editBounds: EditBounds | null = span?.bounds ?? null;

  const refetchListing = useCallback(
    () => queryClient.invalidateQueries({ queryKey: listingDetailsKey(listing.id ?? '') }),
    [queryClient, listing.id],
  );

  const blockMutation = useMutation({
    mutationFn: async ({
      ranges,
      reason,
    }: {
      ranges: readonly { start: string; end: string }[];
      reason: string;
    }) => {
      for (const r of ranges) {
        await addUnavailability({
          listingId: listing.id ?? '',
          start: r.start,
          end: r.end,
          reason,
        });
      }
      return ranges.length;
    },
    onSuccess: (count) => {
      dispatch({ type: 'result', flow: { kind: 'blocked', count } });
      void refetchListing();
    },
    onError: () => {
      void refetchListing();
      dispatch({ type: 'result', flow: { kind: 'conflict' } });
    },
  });

  const mutation = useMutation({
    mutationFn: (vars: { start: string; end: string; agentUserId: string | null }) =>
      bookSlot({
        listingId: listing.id ?? '',
        start: vars.start,
        end: vars.end,
        ...(vars.agentUserId ? { agentUserId: vars.agentUserId } : {}),
      }),
    onSuccess: (data, vars) => {
      handleResult(data.bookSlot, vars);
    },
    onError: () => {
      dispatch({
        type: 'result',
        flow: { kind: 'error', message: 'We couldn’t book that slot. Please try again.' },
      });
    },
  });

  function handleResult(
    result: BookSlotResult,
    vars: { start: string; end: string; agentUserId: string | null },
  ) {
    if (!result) {
      dispatch({
        type: 'result',
        flow: { kind: 'error', message: 'We couldn’t book that slot. Please try again.' },
      });
      return;
    }
    switch (result.__typename) {
      case 'Booking': {
        const bookedFor = vars.agentUserId
          ? (nameMap.get(vars.agentUserId) ?? 'the selected agent')
          : null;
        dispatch({
          type: 'result',
          flow: {
            kind: 'booked',
            range: { start: result.startsAt ?? vars.start, end: result.endsAt ?? vars.end },
            forAgent: bookedFor,
          },
        });
        onBooked?.();
        void refetchListing();
        break;
      }
      case 'SlotUnavailableError':
        dispatch({
          type: 'result',
          flow: {
            kind: 'slot_taken',
            message: result.message ?? 'That slot was just taken. Pick another time.',
          },
        });
        break;
      case 'OutsideBookingWindowError':
        dispatch({
          type: 'result',
          flow: {
            kind: 'outside_window',
            message: result.message ?? 'That time is outside the listing’s booking window.',
          },
        });
        break;
      default: {
        const _exhaustive: never = result;
        void _exhaustive;
        dispatch({
          type: 'result',
          flow: { kind: 'error', message: 'We couldn’t book that slot. Please try again.' },
        });
      }
    }
  }


  const paint = useCallback((next: Set<CellKey>) => dispatch({ type: 'paint', cells: next }), []);

  const release = useCallback((point: PopoverPoint | null) => {
    dispatch({
      type: 'release',
      anchor: point ?? { x: globalThis.innerWidth / 2, y: globalThis.innerHeight / 2 },
    });
  }, []);

  const setMode = useCallback(
    (next: CalendarMode) => {
      if (next === 'block' && !canBlock) return;
      dispatch({ type: 'set_mode', mode: next });
    },
    [canBlock],
  );

  const setAssignee = useCallback(
    (next: string | null) => dispatch({ type: 'set_assignee', assignee: next }),
    [],
  );

  function adjustSelection(edge: 'start' | 'end', delta: -1 | 1) {
    const next = span?.nudge(edge, delta);
    if (next) dispatch({ type: 'repaint', cells: next });
  }

  function submitBooking() {
    if (selection.kind !== 'ok') return;
    if (needsAssignee) return;
    if (rangeConflicts(selection.range, bookings, unavailability)) {
      dispatch({
        type: 'result',
        flow: { kind: 'slot_taken', message: 'That time overlaps an existing booking. Pick another.' },
      });
      return;
    }
    mutation.mutate({
      start: selection.range.start.toISOString(),
      end: selection.range.end.toISOString(),
      agentUserId: canBookForOthers ? assignee : null,
    });
  }

  function commitBlock(reason: string) {
    if (blockRanges.length === 0) return;
    const conflicts = blockRanges.some((r) => rangeConflicts(toRange(r), bookings, unavailability));
    if (conflicts) {
      dispatch({ type: 'result', flow: { kind: 'conflict' } });
      return;
    }
    blockMutation.mutate({ ranges: blockRanges, reason });
  }

  const closeCard = useCallback(() => dispatch({ type: 'close' }), []);

  const card: CardVM =
    state.anchor === null
      ? null
      : state.flow.kind === 'booked' || state.flow.kind === 'blocked'
        ? {
            phase: 'settled',
            anchor: state.anchor,
            zone,
            flow: state.flow,
            onClose: closeCard,
          }
        : {
            phase: 'editing',
            anchor: state.anchor,
            zone,
            tapMode,
            mode,
            canToggleMode: canBlock,
            onModeChange: setMode,
            selection,
            editTimes,
            editBounds,
            onAdjust: adjustSelection,
            canBookForOthers,
            agents,
            viewerUserId,
            viewerBookable,
            assignee,
            needsAssignee,
            onAssigneeChange: setAssignee,
            assigneeLabel,
            onBook: submitBooking,
            submitting: mutation.isPending,
            blockRanges,
            onBlock: commitBlock,
            blocking: blockMutation.isPending,
            flow: state.flow,
            onClose: closeCard,
          };

  return {
    mode,
    canToggleMode: canBlock,
    setMode,
    anchor,
    range,
    setRange,
    step,
    goToToday,
    rangeForced,
    days,
    columns,
    rows,
    canStepBack,
    canStepForward,
    painted: state.painted,
    paint,
    release,
    cellState,
    selection,
    blockRanges,
    editTimes,
    editBounds,
    adjustSelection,
    card,
    closeCard,
    flow: state.flow,
    submitBooking,
    submitting: mutation.isPending,
    commitBlock,
    blocking: blockMutation.isPending,
    canBookForOthers,
    assignee,
    setAssignee,
    assigneeLabel,
    agents,
    viewerUserId,
    tapMode,
    gridWrapRef,
    cardRef,
  };
}

export type BookingIntake = ReturnType<typeof useBookingIntake>;

export type CardVM =
  | ({ readonly phase: 'editing' } & EditingCard)
  | ({ readonly phase: 'settled' } & SettledCard)
  | null;

export interface EditingCard {
  readonly anchor: PopoverPoint;
  readonly zone: string | null;
  readonly tapMode: boolean;
  readonly mode: CalendarMode;
  readonly canToggleMode: boolean;
  readonly onModeChange: (mode: CalendarMode) => void;
  readonly selection: SelectionResult;
  readonly editTimes: EditTimes | null;
  readonly editBounds: EditBounds | null;
  readonly onAdjust: (edge: 'start' | 'end', delta: -1 | 1) => void;
  readonly canBookForOthers: boolean;
  readonly agents: readonly Agent[];
  readonly viewerUserId: string | null;
  readonly viewerBookable: boolean;
  readonly assignee: string | null;
  readonly needsAssignee: boolean;
  readonly onAssigneeChange: (agentUserId: string | null) => void;
  readonly assigneeLabel: string | null;
  readonly onBook: () => void;
  readonly submitting: boolean;
  readonly blockRanges: readonly { start: string; end: string }[];
  readonly onBlock: (reason: string) => void;
  readonly blocking: boolean;
  readonly flow: EditingFlow;
  readonly onClose: () => void;
}

export interface SettledCard {
  readonly anchor: PopoverPoint;
  readonly zone: string | null;
  readonly flow: SettledFlow;
  readonly onClose: () => void;
}
